'use client'

import { useParams } from 'next/navigation'
import { useEffect } from 'react'

import { range } from '@/utils/array'
import {
  trackTypeLocalSearch,
  trackViewDataIndukKabKot,
} from '@/utils/tracker/modules/datainduk'
import { TrackerSection, TrackerSubSection } from '@/utils/tracker/shared'
import { DistrictAncestorsResponse } from '@/apis/types'
import { useFetchYayasanStatsByParentWilayah } from '@/apis/useDataIndukYayasan'

import { Container } from '@/components/Container'
import { EmptyState } from '@/components/EmptyState'
import { HeadingTitleBlock } from '@/components/HeadingTitleBlock/HeadingTitleBlock'
import { DataIndukIcon } from '@/components/Icons/DataIndukIcon'

import { DATA_INDUK_YAYASAN_ROUTE, METATAG } from '@/configs/constants'

import { AreaCard } from '../components/AreaCard/AreaCard'
import { AreaCardLoader } from '../components/AreaCard/AreaCardLoader'
import { AreaSummaryStatsYayasan } from '../components/AreaSummaryStats/AreaSummaryStatsYayasan'
import { DataIndukSearchBox } from '../components/DataIndukSearchBox'
import { LastUpdatedAt } from '../components/LastUpdatedAt'
import { LocalSearch } from '../components/LocalSearch'
import { NavSection } from '../components/NavSection'
import { TitlePage } from '../components/TitlePage'
import { DATA_INDUK_TITLE_PREFIX, VARIANT } from '../constant'
import {
  getLatestWilayahName,
  getLogoProvinceLink,
  sumTotalStatsYayasan,
} from '../helpers'
import { useFilterState } from '../hooks/useFilterState'
import { useKabKotSwicher } from '../hooks/useKabKotSwicher'
import { KODE_WILAYAH_LUAR_NEGERI } from '../provinces'

export const YayasanKabKot = ({
  dataBreadcrumb,
}: {
  dataBreadcrumb: DistrictAncestorsResponse
}) => {
  const params = useParams()
  const provinceId = (params?.provinsi_id as string) || ''
  const kabKotId = (params?.kabkot_id as string) || ''

  const { getFilterSearchParamsFromQuery } = useFilterState({
    variant: VARIANT.YAYASAN,
    view: 'list',
  })

  const { data: dataStatsByParent, isPending: isPendingStatsByParent } =
    useFetchYayasanStatsByParentWilayah(kabKotId, {
      enabled: !!kabKotId,
    })

  const { filteredData, onSearchByWilayah } = useKabKotSwicher({
    data: dataStatsByParent,
    isPending: isPendingStatsByParent,
  })

  const isLuarNegeri = provinceId === KODE_WILAYAH_LUAR_NEGERI

  const handleSubmitLocalSearch = (keywordLocalSearch: string) => {
    if (keywordLocalSearch !== '') {
      trackTypeLocalSearch({
        keyword: keywordLocalSearch,
        locationLevel: 'Kabupaten',
        locationId: kabKotId,
        section: TrackerSection.YAYASAN,
        subSection: TrackerSubSection.PAUD_DIKDASMEN,
      })
    }
    onSearchByWilayah(keywordLocalSearch)
  }

  useEffect(() => {
    if (provinceId && kabKotId) {
      trackViewDataIndukKabKot({
        provinceId,
        kabKotId,
        section: TrackerSection.YAYASAN,
        subSection: TrackerSubSection.PAUD_DIKDASMEN,
      })
    }
  }, [provinceId, kabKotId])

  return (
    <>
      <HeadingTitleBlock
        withIcon={true}
        withFaqText={true}
        withMapOverlay={true}
        icon={<DataIndukIcon />}
        title={METATAG.DATA_INDUK_YAYASAN.title}
        desc={METATAG.DATA_INDUK_YAYASAN.description}
        trackerGaEventName="induk"
        trackerPageTitle="Data Induk"
        trackerAdditionalParams={{
          section: TrackerSection.YAYASAN,
          sub_section: TrackerSubSection.PAUD_DIKDASMEN,
        }}
      />
      <article>
        <section className="flex flex-col" id="search-section">
          <DataIndukSearchBox
            source={`${DATA_INDUK_YAYASAN_ROUTE}/${provinceId}/${kabKotId}`}
            locationId={`${kabKotId}`}
            locationLevel="Kabupaten"
            placeholder="Cari Data Yayasan"
            variant="yayasan"
          />
        </section>

        <NavSection
          data={dataBreadcrumb}
          level={2}
          withRoot={true}
          prefixPath={DATA_INDUK_YAYASAN_ROUTE}
          className="pt-4"
        />

        <section className="bg-gray-5 py-4" id="table-section">
          <Container>
            {!dataBreadcrumb ? (
              <div className="mb-4 min-h-[400px] w-full rounded-lg bg-white p-6">
                <EmptyState
                  title="Data tidak ditemukan"
                  description="Maaf data tidak dapat ditemukan, silahkan pilih data lain"
                  size="sm"
                />
              </div>
            ) : (
              <>
                <div
                  className="min-h-[500px] w-full rounded-lg bg-white p-6"
                  data-testid="content-wrapper"
                >
                  <AreaSummaryStatsYayasan
                    withImage={true}
                    isPending={isPendingStatsByParent}
                    total={
                      !isPendingStatsByParent && dataStatsByParent
                        ? sumTotalStatsYayasan(dataStatsByParent?.data)
                        : 0
                    }
                    title={`${DATA_INDUK_TITLE_PREFIX[VARIANT.YAYASAN]} di ${getLatestWilayahName({ data: dataBreadcrumb })}`}
                    image={`${getLogoProvinceLink({
                      data: dataBreadcrumb,
                      withDefault: true,
                    })}`}
                    level={isLuarNegeri ? 'country' : 'district'}
                    wrapperClass="mb-4"
                  />
                  <div className="flex flex-col items-center justify-between gap-2 md:flex-row">
                    <TitlePage variant={VARIANT.YAYASAN} />
                    <LocalSearch onSubmit={handleSubmitLocalSearch} />
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                    {isPendingStatsByParent ? (
                      <>
                        {range(1, 5).map((item) => (
                          <AreaCardLoader key={item} />
                        ))}
                      </>
                    ) : (
                      <>
                        {(filteredData || []).map((item) => {
                          return (
                            <AreaCard
                              key={item.district.kodeWilayah}
                              code={item.district.kodeWilayah}
                              title={item.district.namaWilayah}
                              link={`${DATA_INDUK_YAYASAN_ROUTE}/${provinceId}/${kabKotId}/${item.district.kodeWilayah}`}
                              additionalParams={getFilterSearchParamsFromQuery()}
                              textLink={`Lihat Detail`}
                              stats={item.yayasanPendidikanStatistics}
                              variant="yayasan"
                            />
                          )
                        })}
                      </>
                    )}
                  </div>
                </div>
                {dataStatsByParent?.meta?.lastUpdatedAt && (
                  <LastUpdatedAt
                    value={dataStatsByParent?.meta.lastUpdatedAt}
                  />
                )}
              </>
            )}
          </Container>
        </section>
      </article>
    </>
  )
}
